import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import HeroGallery from '../components/HeroGallery';
import PriceTable from '../components/PriceTable';
import ReviewsSection from '../components/ReviewsSection';
import DescriptorMarquee from '../components/DescriptorMarquee';
import OpenStatusBadge from '../components/OpenStatusBadge';
import Reveal, { RevealDivider } from '../components/Reveal';
import { Calendar, Car, Beef, Cake, UtensilsCrossed, Phone, WineOff, MapPin, Accessibility, Leaf, ChefHat, Users, ArrowRight } from 'lucide-react';

const Flourish: React.FC = () => (
  <div className="flex items-center justify-center gap-3">
    <span className="h-px w-10 bg-luxury-gold/50" />
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-luxury-gold flex-shrink-0">
      <path
        d="M12 2c1.5 2 1.5 4 0 6-1.5-2-1.5-4 0-6zM12 16c1.5 2 1.5 4 0 6-1.5-2-1.5-4 0-6zM2 12c2-1.5 4-1.5 6 0-2 1.5-4 1.5-6 0zM16 12c2-1.5 4-1.5 6 0-2 1.5-4 1.5-6 0zM6.3 6.3c2.2.6 3.5 2 3.9 4.2-2.2-.6-3.5-2-3.9-4.2zM13.8 13.5c2.2.6 3.5 2 3.9 4.2-2.2-.6-3.5-2-3.9-4.2zM17.7 6.3c-.4 2.2-1.7 3.6-3.9 4.2.4-2.2 1.7-3.6 3.9-4.2zM10.2 13.5c-.4 2.2-1.7 3.6-3.9 4.2.4-2.2 1.7-3.6 3.9-4.2z"
        fill="currentColor"
      />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    </svg>
    <span className="h-px w-10 bg-luxury-gold/50" />
  </div>
);

const Home: React.FC = () => {
  const { t } = useTranslation();

  const todayIndex = (() => {
    const jsDay = new Date().getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  })();

  const schedule = [
    { day: t('hours.monday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.tuesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.wednesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.thursday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.friday'), lunch: t('hours.lunch'), dinner: t('hours.dinnerFriday') },
    { day: t('hours.saturday'), lunch: t('hours.lunchWeekend'), dinner: t('hours.dinnerSaturday') },
    { day: t('hours.sunday'), lunch: t('hours.lunchWeekend'), dinner: t('hours.dinner') }
  ];

  const aboutHighlights = [
    { icon: <Leaf size={24} />, text: t('about.freshness') },
    { icon: <ChefHat size={24} />, text: t('about.homemade') },
    { icon: <Users size={24} />, text: t('about.accessible') }
  ];

  const features = [
    { icon: <Calendar size={28} />, text: t('info.open7days') },
    { icon: <Car size={28} />, text: t('info.parking') },
    { icon: <Beef size={28} />, text: t('info.halal') },
    { icon: <Cake size={28} />, text: t('info.birthday') },
    { icon: <UtensilsCrossed size={28} />, text: t('info.vegetarian') },
    { icon: <Phone size={28} />, text: t('info.noReservation') }
  ];

  return (
    <>
      <Hero />

      {/* Gallery Section */}
      <HeroGallery />

      {/* About / Notre Histoire Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative">
        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-10 md:mb-14">
            <p className="text-luxury-gold text-sm tracking-[0.3em] uppercase font-body mb-3">
              {t('nav.about')}
            </p>
            <Flourish />
            <h2 className="text-5xl md:text-6xl font-display text-luxury-black my-3">
              {t('about.historyTitle')}
            </h2>
            <Flourish />
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-5xl mx-auto mb-12">
            <Reveal direction="left">
              <div className="rounded-2xl overflow-hidden shadow-xl h-96 md:h-[480px] lg:h-[560px]">
                <img
                  src="/restaurant_photos/apropo_photo.jpeg"
                  alt={t('about.altRestaurantInterior')}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal direction="right">
              <p className="text-luxury-gold text-xs tracking-[0.2em] uppercase font-body mb-4">
                {t('home.aboutEyebrow')}
              </p>
              <p className="text-xl text-luxury-black font-body leading-relaxed mb-6">
                {t('about.historyText1')}
              </p>
              <p className="text-xl text-luxury-black font-body leading-relaxed mb-8">
                {t('about.historyText4')}
              </p>
              <Link to="/about">
                <button className="inline-flex items-center gap-3 bg-luxury-black text-luxury-gold px-8 py-4 rounded-full font-display text-sm uppercase tracking-widest hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300">
                  {t('nav.about')}
                  <ArrowRight size={16} />
                </button>
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {aboutHighlights.map((item, index) => (
              <Reveal key={index} delay={index * 0.08}>
                <div className="flex items-center gap-4 bg-white rounded-2xl shadow-sm p-6">
                  <div className="text-luxury-gold flex-shrink-0">{item.icon}</div>
                  <div>
                    <span className="block h-px w-6 bg-luxury-gold mb-2" />
                    <p className="text-luxury-black font-body">{item.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <DescriptorMarquee />

      {/* Prices Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative">
        {/* Cherry Blossom Decoration Background */}
        <img
          src="/restaurant_photos/cherry_blossom1.png"
          alt=""
          className="absolute right-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0"
        />
        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">Tarification</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('prices.title')}
            </h2>
            <RevealDivider className="mx-auto w-20" delay={0.15} />
          </Reveal>

          <PriceTable />
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      {/* Hours & Location Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative overflow-hidden">
        <img
          src="/restaurant_photos/gold_fan.png"
          alt=""
          className="absolute right-0 top-1/2 -translate-y-1/2 w-64 md:w-72 lg:w-80 opacity-20 pointer-events-none z-0 rotate-180"
        />
        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-8 md:mb-12 lg:mb-16">
            <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">
              {t('nav.location')}
            </p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('home.hoursAndInfoTitle')}
            </h2>
            <div className="flex justify-center mb-4">
              <OpenStatusBadge />
            </div>
            <RevealDivider className="mx-auto w-20" delay={0.15} />
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            {/* Left: Hours */}
            <Reveal direction="left">
              <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-luxury-gold/20">
                <div className="grid grid-cols-3 bg-luxury-gold/10 border-b border-luxury-gold/20">
                  <div className="p-4 font-display text-luxury-gold text-sm uppercase tracking-wider">Jour</div>
                  <div className="p-4 text-center font-display text-luxury-gold text-sm uppercase tracking-wider">Midi</div>
                  <div className="p-4 text-center font-display text-luxury-gold text-sm uppercase tracking-wider">Soir</div>
                </div>
                {schedule.map((item, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-3 transition-colors duration-300 ${
                      index === todayIndex
                        ? 'bg-luxury-gold/15 font-semibold'
                        : index % 2 === 0 ? 'bg-white' : 'bg-luxury-cream/60'
                    }`}
                  >
                    <div className="p-4 font-body text-lg text-luxury-black flex items-center gap-2">
                      {item.day}
                      {index === todayIndex && <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold" />}
                    </div>
                    <div className="p-4 text-center text-lg text-luxury-black">{item.lunch}</div>
                    <div className="p-4 text-center text-lg text-luxury-black">{item.dinner}</div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-6">
                <p className="font-semibold text-luxury-black">{t('info.open7days')}</p>
                <p className="text-luxury-gray text-sm mt-1">{t('info.noReservation')}</p>
              </div>
            </Reveal>

            {/* Right: Map + Practical Info */}
            <Reveal direction="right">
              <div className="h-[280px] md:h-[320px] rounded-lg overflow-hidden shadow-xl mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2628.4456337899486!2d2.1681019!3d48.7846875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e67d6f0c8c5b63%3A0x3e8e8e8e8e8e8e8e!2s1%20Rue%20Andr%C3%A9%20Citro%C3%ABn%2C%2078140%20V%C3%A9lizy-Villacoublay%2C%20France!5e0!3m2!1sfr!2sfr!4v1703268900000!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pacific 78 Restaurant Location"
                />
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <MapPin className="text-luxury-gold mt-1 flex-shrink-0" size={22} />
                  <div>
                    <p className="font-semibold text-luxury-black">{t('location.address')}</p>
                    <p className="text-luxury-gray-dark text-sm">{t('location.inMall')}</p>
                    <p className="text-luxury-gray-dark text-sm">{t('location.aboveCastorama')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Car className="text-luxury-gold mt-1 flex-shrink-0" size={22} />
                  <p className="text-luxury-gray-dark">{t('info.parking')}</p>
                </div>
                <div className="flex items-start gap-4">
                  <Accessibility className="text-luxury-gold mt-1 flex-shrink-0" size={22} />
                  <p className="text-luxury-gray-dark">{t('info.pmr')}</p>
                </div>
              </div>

              <a
                href="https://maps.app.goo.gl/8Hy52cCZLU2brFfj6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-6 bg-luxury-gold text-luxury-black px-6 py-3 rounded-full font-display text-sm uppercase tracking-wider hover:bg-luxury-champagne transition-all duration-300 shadow-lg"
              >
                {t('location.viewMap')}
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-white relative">
        {/* Cherry Blossom 2 Decoration - Right Side Mirrored */}
        <img
          src="/restaurant_photos/cherry_blossom2.png"
          alt=""
          className="absolute left-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0" style={{ transform: 'translateY(-50%) scaleX(-1) scaleY(-1)' }}
        />

        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">Services</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('info.title')}
            </h2>
            <RevealDivider className="mx-auto w-20" delay={0.15} />
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Reveal key={index} delay={index * 0.06}>
                <div className="flex items-center gap-6 luxury-card p-8 hover-lift group">
                  <div className="text-luxury-gold group-hover:text-luxury-black transition-colors duration-300">{feature.icon}</div>
                  <p className="text-luxury-gray-dark font-body text-lg">{feature.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center mt-16">
            <Link to="/menu">
              <button className="luxury-button">
                {t('hero.cta')}
              </button>
            </Link>
          </Reveal>

          {/* Outside Drinks Policy */}
          <Reveal className="mt-12 max-w-2xl mx-auto">
            <div className="border border-luxury-gold/30 rounded-lg p-6 md:p-8 bg-white text-center shadow-sm">
              <div className="flex items-center justify-center gap-2 mb-4">
                <WineOff className="text-luxury-gold flex-shrink-0" size={24} />
                <h4 className="text-xl font-display text-luxury-black">
                  {t('outsideDrinks.title')}
                </h4>
              </div>
              <p className="text-luxury-gray-dark font-body text-base mb-3">
                {t('outsideDrinks.rule')}
              </p>
              <p className="text-luxury-black font-body text-base font-semibold mb-3">
                {t('outsideDrinks.fee')}
              </p>
              <p className="text-luxury-gray-dark font-body text-sm italic">
                {t('outsideDrinks.thanks')}
              </p>
            </div>
          </Reveal>

          {/* Waste Policy Warning */}
          <Reveal delay={0.1} className="mt-8 max-w-2xl mx-auto">
            <div className="border border-luxury-gold/30 rounded-lg p-6 bg-white text-center shadow-sm">
              <p className="text-luxury-black font-body text-base mb-2">
                {t('prices.wasteWarning')}
              </p>
              <p className="text-luxury-gray-dark font-body text-sm">
                {t('prices.noTakeaway')}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-gray-dark text-luxury-champagne relative overflow-hidden">
        <img
          src="/restaurant_photos/apropo_photo.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-30"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-luxury-black/80" />
        <div className="section-padding text-center relative z-10">
          <RevealDivider className="mx-auto mb-8 w-16" />
          <Reveal delay={0.1}>
            <h2 className="text-display-md md:text-display-lg font-display mb-8 text-luxury-champagne">
              {t('cta.title')}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-xl mb-12 max-w-3xl mx-auto font-body leading-relaxed text-luxury-champagne/80 text-center text-balance">
              {t('cta.text')}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              href="https://maps.app.goo.gl/8Hy52cCZLU2brFfj6"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="bg-luxury-gold text-luxury-black px-10 py-5 font-display tracking-wider uppercase text-sm hover:bg-luxury-champagne transition-all duration-500 border border-luxury-gold">
                {t('cta.button')}
              </button>
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Home;
